import { baseApi } from "./baseApi";

// Normalise /task/action/events rows across actions — each returns a
// DIFFERENT shape:
//   • active / recent → TASK-shaped   (``task_id`` / ``task_title`` /
//                                       ``created_at`` / ``status``)
//   • coming          → TRIGGER-shaped (``trigger_id`` / ``trigger_title``
//                                        / ``triggerTime`` / ``scheduleType``)
// Coalesce the identity + title onto ``template_id`` / ``template_title``
// so consumers (HomeWhatsHappeningCard) can read one field set for every
// tab. Without this, rows rendered blank titles + broken/duplicate keys,
// and the tab filters dropped them. Exported so the transform is
// unit-testable without RTK Query plumbing.
export function normalizeActionEventRows(data) {
  const rows = Array.isArray(data) ? data : data?.tasks || data?.events || [];
  return rows.map((r) => ({
    ...r,
    template_id: r.template_id || r.task_id || r.trigger_id,
    template_title: r.template_title || r.task_title || r.trigger_title,
  }));
}

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Items the user must act on (Home "Actions required" panel + badge).
    // BE returns either an array or a `{ items | rows }` wrapper — normalize
    // here so consumers always see a plain array.
    getActionsRequired: builder.query({
      query: () => ({ url: "/task/actions-required", method: "GET" }),
      transformResponse: (data) =>
        Array.isArray(data) ? data : data?.items || data?.rows || [],
      providesTags: [{ type: "Tasks", id: "ACTIONS_REQUIRED" }],
    }),

    // Per-tab task list on Home. action ∈ "coming" | "active" | "recent".
    // BE 404 means "nothing for this tab" — surfaced via the `error` field
    // so the consumer can treat it as an empty list.
    getActionEvents: builder.query({
      query: (action) => ({
        url: "/task/action/events",
        method: "GET",
        params: { action },
      }),
      // Normalise row shape across actions — see normalizeActionEventRows.
      transformResponse: normalizeActionEventRows,
      providesTags: (result, error, action) => [
        { type: "Tasks", id: `EVENTS_${action}` },
      ],
    }),

    // NOTE: the recent-tasks list (formerly `getRecentTasks` → GET
    // /task/) was removed. The Sidebar's "My Tasks" is now fed entirely
    // by the `/task/recent/events` SSE stream into `recentTasksSlice`
    // (see useRecentTasksStream) — no GET /task/ and no polling.

    // Delete one or more tasks. BE expects DELETE /task/ with a
    // `{ task_ids: [...] }` body — axios forwards the body via the
    // `data` config field. Accepts either a single id (string) or
    // an array; normalises to an array internally. Invalidates the
    // bare `Tasks` tag so the Home Tasks/* providers (ACTIONS_REQUIRED +
    // EVENTS_*) refetch; the Sidebar list reconciles via the stream (and
    // the call site removes the row optimistically for instant feedback).
    deleteTasks: builder.mutation({
      query: (taskIds) => ({
        url: "/task/",
        method: "DELETE",
        data: {
          task_ids: Array.isArray(taskIds) ? taskIds : [taskIds],
        },
      }),
      invalidatesTags: ["Tasks"],
    }),

    // R1 (2026-05-27): cross-feature cancel for a running task — used
    // by TriggerCard's in-card cancel button when a trigger is
    // currently running a task. BE expects `task_id` as a query
    // string, not a body. Invalidates both `Tasks` (so Sidebar +
    // Home refresh) and `Triggers` (so the card flips out of
    // "running" status straight away instead of waiting for the
    // next 10 s poll).
    cancelTask: builder.mutation({
      query: (taskId) => ({
        url: "/task/cancel",
        method: "POST",
        params: { task_id: taskId },
      }),
      invalidatesTags: ["Tasks", "Triggers"],
    }),
  }),
});

export const {
  useGetActionsRequiredQuery,
  useGetActionEventsQuery,
  useDeleteTasksMutation,
  useCancelTaskMutation,
} = tasksApi;
